import { Button } from "@/src/components/buttons";
import { openDialog, closeDialog } from "@/src/components/dialogs";
import { Patient } from "@/src/data/allTypes";
import apiFetch from "@/src/utils/apiFetch";
import {
  getServiceId,
  getUserId,
  getName,
  formatDate,
} from "@/src/utils/utils";
import { openMaxPatientsDialog } from "../admin/users/noLicenceDialog";
import { openDuplicatePatientsPicker } from "./duplicatePatientsPicker";

export async function handlePatientCreationError({
  patient,
  errorMessage = null,
  shouldOpenPatientPicker = false,
  onSelect,
}: {
  patient: Patient;
  errorMessage: string | null;
  shouldOpenPatientPicker?: boolean;
  onSelect?: (patient: Patient) => void;
}) {
  console.log("errorMessage", errorMessage);
  if (!errorMessage) return;
  if (errorMessage == "maxPatientsReached")
    return await openMaxPatientsDialog();
  if (errorMessage != "patientAlreadyExists") throw new Error(errorMessage);
  if (shouldOpenPatientPicker) {
    return await openDuplicatePatientsPicker({
      current: patient,
      onSelect: async (selectedPatient) => {
        if (!selectedPatient) {
          patient = await apiFetch("patients", "addOne", {
            patient,
            serviceId: getServiceId(),
            userId: getUserId(),
            shouldOverwrite: true,
          });
          onSelect?.(patient);
          return;
        }
        onSelect?.(selectedPatient);
      },
    });
  }

  return await openExistingPatientAlertDialog({
    patient,
    onSelectOld: async (patient) => {
      await apiFetch("patients", "addServiceAccessToPatient", {
        patientId: patient.id,
        serviceId: getServiceId(),
        userId: getUserId(),
      });

      onSelect?.(patient);
    },
    onCreateNew: (patient) => {
      onSelect?.(patient);
    },
    onCancel: () => {},
  });
}

async function openExistingPatientAlertDialog({
  patient,
  onCancel,
  onCreateNew,
  onSelectOld,
}: {
  patient: Patient;
  onCreateNew: (patient: Patient) => void;
  onSelectOld: (patient: Patient) => void;
  onCancel: (patient: Patient) => void;
}): Promise<boolean> {
  let result = true;
  await openDialog({
    id: "patientExistsDialog",
    title: "Le patient existe déjà",
    content: (
      <div className="c1 gap10 ">
        <div className="bold">
          Le patient {getName(patient)} né le {formatDate(patient.birth, "LL")}{" "}
          existe Déjà
        </div>
        <div className="gray">
          Voulez-vous créer un nouveau dossier ou selectionner le patient
          existant ?
        </div>
      </div>
    ),
    footer: (
      <div className="c3 gap10 p15">
        <Button
          color="red"
          type={"tertiary"}
          onClick={() => {
            result = false;
            onCancel(patient);
            closeDialog("patientExistsDialog");
          }}
        >
          Annuler
        </Button>

        <Button
          type={"tertiary"}
          onClick={async () => {
            try {
              let newPatient = await apiFetch("patients", "addOne", {
                patient,
                serviceId: getServiceId(),
                userId: getUserId(),
                shouldOverwrite: true,
              });
              onCreateNew(newPatient);
              closeDialog("patientExistsDialog");
            } catch (e: any) {
              await handlePatientCreationError({
                patient,
                errorMessage: e.message,
                onSelect: (patient) => {
                  onCreateNew(patient);
                },
              });
              closeDialog("patientExistsDialog");
            }
          }}
        >
          Créer un nouveau
        </Button>
        <Button
          onClick={async () => {
            let oldPatient = await apiFetch("patients", "loadExistingPatient", {
              lastName: patient.lastName,
              firstName: patient.firstName,
              birth: patient.birth,
            });

            if (!oldPatient)
              throw new Error("Impossible de charger le patient");
            onSelectOld(oldPatient);
            closeDialog("patientExistsDialog");
          }}
        >
          Selectionner le patient existant
        </Button>
      </div>
    ),
  });
  return result;
}
